import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, Check, X, Search, ChevronDown } from 'lucide-react';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const ALL_DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
const calcDietCals = (meals) =>
  (meals || []).reduce((sum, m) => sum + (m.foods || []).reduce((s, f) => s + (Number(f.calories) || 0), 0), 0);

const emptyFood = () => ({ name: '', portion: '100g', calories: '', protein: '', carbs: '', fat: '', fiber: '' });
const emptyDietFood = () => ({ name: '', quantity: '', calories: '', protein: '', carbs: '', fat: '', preparation: 'cru' });
const emptyMeal = () => ({ name: '', time: '', days: [...ALL_DAYS], foods: [emptyDietFood()] });

export default function AdminDespensa() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [mainTab, setMainTab] = useState('foods');

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyFood());
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');

  // Diet templates state
  const [dietTemplates, setDietTemplates] = useState([]);
  const [showAddDiet, setShowAddDiet] = useState(false);
  const [expandedDiet, setExpandedDiet] = useState(null);
  const [dietForm, setDietForm] = useState({ name: '', description: '', notes: '', meals: [emptyMeal()] });
  const [dietSaving, setDietSaving] = useState(false);
  const [editingDiet, setEditingDiet] = useState(null);
  const [editDietForm, setEditDietForm] = useState({});
  const [foodSearch, setFoodSearch] = useState({});
  const [foodSuggestions, setFoodSuggestions] = useState({});
  const debounceTimers = useRef({});

  useEffect(() => {
    if (!token) { navigate('/admin'); return; }
    load();
    loadDiets();
  // eslint-disable-next-line
  }, []);

  const load = async (q = '') => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/despensa`, {
        headers, params: q ? { search: q } : {},
      });
      setItems(res.data);
    } catch {}
  };

  const loadDiets = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/diet-templates`, { headers });
      setDietTemplates(res.data);
    } catch {}
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/despensa`, form, { headers });
      setItems(prev => [res.data, ...prev]);
      setForm(emptyFood());
    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao adicionar.'); }
    finally { setSaving(false); }
  };

  const handleDelete = (id) => {
    setConfirm({
      message: 'Remover este alimento?',
      onConfirm: async () => {
        try {
          await axios.delete(`${BACKEND_URL}/api/admin/despensa/${id}`, { headers });
          setItems(prev => prev.filter(i => i.id !== id));
        } catch { setAlertMsg('Erro ao remover.'); }
      },
    });
  };

  const startEdit = (item) => {
    setEditing(item.id);
    setEditForm({ ...item });
  };

  const saveEdit = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/despensa/${id}`, editForm, { headers });
      setItems(prev => prev.map(i => i.id === id ? res.data : i));
      setEditing(null);
    } catch { setAlertMsg('Erro ao salvar.'); }
  };

  // Diet form helpers
  const setDietMeal = (mi, field, val) => setDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, [field]: val } : m) }));
  const addDietMeal = () => setDietForm(f => ({ ...f, meals: [...f.meals, emptyMeal()] }));
  const removeDietMeal = (mi) => setDietForm(f => ({ ...f, meals: f.meals.filter((_, j) => j !== mi) }));
  const setDietFood = (mi, fi, field, val) => setDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((fd, k) => k === fi ? { ...fd, [field]: val } : fd) } : m) }));
  const addDietFood = (mi) => setDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: [...m.foods, emptyDietFood()] } : m) }));
  const removeDietFood = (mi, fi) => setDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.filter((_, k) => k !== fi) } : m) }));

  const handleFoodSearchChange = (mi, fi, val) => {
    const key = `${mi}_${fi}`;
    setFoodSearch(prev => ({ ...prev, [key]: val }));
    setDietFood(mi, fi, 'name', val);
    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
    if (!val.trim()) { setFoodSuggestions(prev => ({ ...prev, [key]: [] })); return; }
    debounceTimers.current[key] = setTimeout(async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/despensa`, { headers, params: { search: val } });
        setFoodSuggestions(prev => ({ ...prev, [key]: res.data || [] }));
      } catch { setFoodSuggestions(prev => ({ ...prev, [key]: [] })); }
    }, 300);
  };

  const selectFoodSuggestion = (mi, fi, item) => {
    const key = `${mi}_${fi}`;
    setDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((fd, k) => k === fi ? {
      ...fd,
      name: item.name,
      calories: item.calories ? String(item.calories) : '',
      protein: item.protein ? String(item.protein) : '',
      carbs: item.carbs ? String(item.carbs) : '',
      fat: item.fat ? String(item.fat) : '',
      quantity: item.portion || '',
    } : fd) } : m) }));
    setFoodSearch(prev => ({ ...prev, [key]: item.name }));
    setFoodSuggestions(prev => ({ ...prev, [key]: [] }));
  };

  const handleAddDiet = async (e) => {
    e.preventDefault();
    setDietSaving(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/diet-templates`, { ...dietForm, calories: calcDietCals(dietForm.meals) }, { headers });
      setDietTemplates(prev => [res.data, ...prev]);
      setDietForm({ name: '', description: '', notes: '', meals: [emptyMeal()] });
      setShowAddDiet(false);
    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao criar dieta.'); }
    finally { setDietSaving(false); }
  };

  const startEditDiet = (dt) => {
    setEditingDiet(dt.id);
    setEditDietForm({ ...dt, meals: (dt.meals || [emptyMeal()]).map(m => ({ ...m, days: m.days || [...ALL_DAYS], foods: (m.foods || [emptyDietFood()]).map(f => ({ ...f, preparation: f.preparation || 'cru' })) })) });
    setExpandedDiet(dt.id);
  };

  const saveEditDiet = async (id) => {
    try {
      const res = await axios.put(`${BACKEND_URL}/api/admin/diet-templates/${id}`, { ...editDietForm, calories: calcDietCals(editDietForm.meals) }, { headers });
      setDietTemplates(prev => prev.map(d => d.id === id ? res.data : d));
      setEditingDiet(null);
    } catch { setAlertMsg('Erro ao salvar dieta.'); }
  };

  // Edit diet form helpers
  const setEditMeal = (mi, field, val) => setEditDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, [field]: val } : m) }));
  const addEditMeal = () => setEditDietForm(f => ({ ...f, meals: [...f.meals, emptyMeal()] }));
  const removeEditMeal = (mi) => setEditDietForm(f => ({ ...f, meals: f.meals.filter((_, j) => j !== mi) }));
  const setEditFood = (mi, fi, field, val) => setEditDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((fd, k) => k === fi ? { ...fd, [field]: val } : fd) } : m) }));
  const addEditFood = (mi) => setEditDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: [...m.foods, emptyDietFood()] } : m) }));
  const removeEditFood = (mi, fi) => setEditDietForm(f => ({ ...f, meals: f.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.filter((_, k) => k !== fi) } : m) }));

  const handleDeleteDiet = (id) => {
    setConfirm({
      message: 'Remover esta dieta?',
      onConfirm: async () => {
        try {
          await axios.delete(`${BACKEND_URL}/api/admin/diet-templates/${id}`, { headers });
          setDietTemplates(prev => prev.filter(d => d.id !== id));
        } catch { setAlertMsg('Erro ao remover.'); }
      },
    });
  };

  const inputCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
  const labelCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold uppercase tracking-tight">Despensa</h1>
            <p className="text-xs text-[#555] mt-0.5">Biblioteca global de alimentos e dietas</p>
          </div>
        </div>

        {/* Main tabs */}
        <div className="flex border-b border-white/10 mb-6">
          {[['foods','Alimentos'],['diets','Dietas']].map(([val,label]) => (
            <button key={val} onClick={() => setMainTab(val)}
              className={`px-6 py-3 text-sm font-medium uppercase tracking-wider border-b-2 -mb-px transition-colors ${mainTab===val ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
            >{label}</button>
          ))}
        </div>

        {mainTab === 'foods' && (<>

        {/* Add form */}
        <div className="bg-[#121212] border border-white/5 p-6 mb-6">
          <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4 flex items-center gap-2">
            <Plus size={13} /> Adicionar alimento
          </h2>
          <form onSubmit={handleAdd} className="flex flex-col gap-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Nome</label>
                <input type="text" required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className={inputCls} placeholder="Frango grelhado" />
              </div>
              <div>
                <label className={labelCls}>Porção</label>
                <input type="text" value={form.portion} onChange={e => setForm(f => ({...f, portion: e.target.value}))} className={inputCls} placeholder="100g" />
              </div>
              <div>
                <label className={labelCls}>Kcal</label>
                <input type="number" step="0.1" value={form.calories} onChange={e => setForm(f => ({...f, calories: e.target.value}))} className={inputCls} placeholder="165" />
              </div>
              <div>
                <label className={labelCls}>Proteína (g)</label>
                <input type="number" step="0.1" value={form.protein} onChange={e => setForm(f => ({...f, protein: e.target.value}))} className={inputCls} placeholder="31" />
              </div>
              <div>
                <label className={labelCls}>Carb (g)</label>
                <input type="number" step="0.1" value={form.carbs} onChange={e => setForm(f => ({...f, carbs: e.target.value}))} className={inputCls} placeholder="0" />
              </div>
              <div>
                <label className={labelCls}>Gordura (g)</label>
                <input type="number" step="0.1" value={form.fat} onChange={e => setForm(f => ({...f, fat: e.target.value}))} className={inputCls} placeholder="3.6" />
              </div>
              <div>
                <label className={labelCls}>Fibra (g)</label>
                <input type="number" step="0.1" value={form.fiber} onChange={e => setForm(f => ({...f, fiber: e.target.value}))} className={inputCls} placeholder="0" />
              </div>
            </div>
            <button type="submit" disabled={saving} className="self-start px-6 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
              {saving ? 'Adicionando...' : 'Adicionar'}
            </button>
          </form>
        </div>

        {/* Search */}
        <div className="flex items-center gap-2 bg-[#121212] border border-white/10 px-3 py-2 mb-4">
          <Search size={13} className="text-[#555]" />
          <input type="text" placeholder="Buscar alimento..." value={search}
            onChange={e => { setSearch(e.target.value); load(e.target.value); }}
            className="bg-transparent outline-none text-sm flex-1 text-[#E5E5E5] placeholder-[#555]"
          />
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-2 pr-4 text-xs text-[#555] uppercase tracking-widest font-medium">Alimento</th>
                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Porção</th>
                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Kcal</th>
                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Prot</th>
                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Carb</th>
                <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Gord</th>
                <th className="py-2 pl-2 w-16"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr key={item.id} className="border-b border-white/5">
                  {editing === item.id ? (
                    <>
                      <td className="py-2 pr-2"><input value={editForm.name} onChange={e => setEditForm(f => ({...f, name: e.target.value}))} className={inputCls} /></td>
                      <td className="py-2 px-1"><input value={editForm.portion} onChange={e => setEditForm(f => ({...f, portion: e.target.value}))} className={inputCls} /></td>
                      <td className="py-2 px-1"><input type="number" value={editForm.calories} onChange={e => setEditForm(f => ({...f, calories: e.target.value}))} className={inputCls} /></td>
                      <td className="py-2 px-1 hidden sm:table-cell"><input type="number" value={editForm.protein} onChange={e => setEditForm(f => ({...f, protein: e.target.value}))} className={inputCls} /></td>
                      <td className="py-2 px-1 hidden sm:table-cell"><input type="number" value={editForm.carbs} onChange={e => setEditForm(f => ({...f, carbs: e.target.value}))} className={inputCls} /></td>
                      <td className="py-2 px-1 hidden sm:table-cell"><input type="number" value={editForm.fat} onChange={e => setEditForm(f => ({...f, fat: e.target.value}))} className={inputCls} /></td>
                      <td className="py-2 pl-2">
                        <div className="flex gap-1">
                          <button onClick={() => saveEdit(item.id)} className="text-green-400 hover:text-green-300"><Check size={14} /></button>
                          <button onClick={() => setEditing(null)} className="text-[#555] hover:text-white"><X size={14} /></button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-3 pr-4 font-medium">{item.name}</td>
                      <td className="py-3 px-2 text-center text-[#A3A3A3]">{item.portion}</td>
                      <td className="py-3 px-2 text-center text-[#A3A3A3]">{item.calories || '—'}</td>
                      <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{item.protein ? `${item.protein}g` : '—'}</td>
                      <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{item.carbs ? `${item.carbs}g` : '—'}</td>
                      <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{item.fat ? `${item.fat}g` : '—'}</td>
                      <td className="py-3 pl-2">
                        <div className="flex gap-2">
                          <button onClick={() => startEdit(item)} className="text-[#555] hover:text-white transition-colors"><Edit2 size={13} /></button>
                          <button onClick={() => handleDelete(item.id)} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan="7" className="py-12 text-center text-[#555] text-sm">Nenhum alimento cadastrado ainda.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        </>)}

        {/* ── DIETAS TAB ── */}
        {mainTab === 'diets' && (
          <div className="flex flex-col gap-6">
            <div className="flex justify-end">
              <button onClick={() => setShowAddDiet(v => !v)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"
              >
                <Plus size={14} /> {showAddDiet ? 'Cancelar' : 'Nova dieta'}
              </button>
            </div>

            {showAddDiet && (
              <form onSubmit={handleAddDiet} className="bg-[#121212] border border-white/5 p-6 flex flex-col gap-4">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3]">Nova dieta</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Nome</label>
                    <input required value={dietForm.name} onChange={e => setDietForm(f => ({...f, name: e.target.value}))} className={inputCls} placeholder="Dieta Low Carb" />
                  </div>
                  <div>
                    <label className={labelCls}>Calorias totais (calculado)</label>
                    <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full">
                      <span className="text-white font-medium">{calcDietCals(dietForm.meals)} kcal</span>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Descrição</label>
                    <input value={dietForm.description} onChange={e => setDietForm(f => ({...f, description: e.target.value}))} className={inputCls} placeholder="Opcional" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Observações</label>
                    <textarea value={dietForm.notes} onChange={e => setDietForm(f => ({...f, notes: e.target.value}))} className={`${inputCls} min-h-[60px] resize-y`} placeholder="Opcional" />
                  </div>
                </div>

                {/* Meals builder */}
                <div className="flex flex-col gap-3 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-[#555] uppercase tracking-widest">Refeições</span>
                    <button type="button" onClick={addDietMeal}
                      className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
                      <Plus size={11} /> Refeição
                    </button>
                  </div>
                  {dietForm.meals.map((meal, mi) => (
                    <div key={mi} className="bg-[#0A0A0A] border border-white/5 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs text-[#555]">Refeição {mi + 1}</span>
                        {dietForm.meals.length > 1 && (
                          <button type="button" onClick={() => removeDietMeal(mi)} className="text-[#555] hover:text-red-400"><Trash2 size={12} /></button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className={labelCls}>Nome</label>
                          <input value={meal.name} onChange={e => setDietMeal(mi, 'name', e.target.value)} className={inputCls} placeholder="Café da manhã" />
                        </div>
                        <div>
                          <label className={labelCls}>Horário sugerido</label>
                          <input type="time" value={meal.time} onChange={e => setDietMeal(mi, 'time', e.target.value)} className={inputCls} />
                        </div>
                      </div>
                      {/* Days of week */}
                      <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                        <span className={`${labelCls} mb-0 mr-1`}>Dias:</span>
                        {ALL_DAYS.map(d => {
                          const active = (meal.days || ALL_DAYS).includes(d);
                          return (
                            <button key={d} type="button" onClick={() => {
                              const cur = meal.days || ALL_DAYS;
                              setDietMeal(mi, 'days', active ? cur.filter(x => x !== d) : [...cur, d]);
                            }} className={`px-2 py-0.5 text-xs border transition-colors ${active ? 'border-white/40 text-white' : 'border-white/10 text-[#444] hover:text-[#888]'}`}>
                              {d.slice(0, 3)}
                            </button>
                          );
                        })}
                        <span className="text-xs text-[#444] ml-1">{(meal.days || ALL_DAYS).length}×/sem</span>
                      </div>

                      {/* Foods */}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#555]">Alimentos</span>
                          <button type="button" onClick={() => addDietFood(mi)}
                            className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-1 transition-colors">
                            <Plus size={11} /> Alimento
                          </button>
                        </div>
                        {meal.foods.map((food, fi) => {
                          const sugKey = `${mi}_${fi}`;
                          return (
                            <div key={fi} className="bg-[#121212] border border-white/5 p-3">
                              <div className="flex justify-end mb-2">
                                {meal.foods.length > 1 && (
                                  <button type="button" onClick={() => removeDietFood(mi, fi)} className="text-[#555] hover:text-red-400"><Trash2 size={11} /></button>
                                )}
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                                <div className="col-span-2 relative">
                                  <label className={labelCls}>Alimento</label>
                                  <input
                                    value={foodSearch[sugKey] !== undefined ? foodSearch[sugKey] : food.name}
                                    onChange={e => handleFoodSearchChange(mi, fi, e.target.value)}
                                    className={inputCls} placeholder="Buscar da despensa..." autoComplete="off"
                                  />
                                  {foodSuggestions[sugKey] && foodSuggestions[sugKey].length > 0 && (
                                    <div className="absolute z-10 left-0 right-0 top-full bg-[#1A1A1A] border border-white/10 max-h-40 overflow-y-auto">
                                      {foodSuggestions[sugKey].map((item, si) => (
                                        <button key={si} type="button"
                                          onMouseDown={() => selectFoodSuggestion(mi, fi, item)}
                                          className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center justify-between gap-2"
                                        >
                                          <span>{item.name}</span>
                                          <span className="text-xs text-[#555]">{item.calories ? `${item.calories}kcal` : ''}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <label className={labelCls}>Qtd</label>
                                  <input value={food.quantity} onChange={e => setDietFood(mi, fi, 'quantity', e.target.value)} className={inputCls} placeholder="100g" />
                                </div>
                                <div>
                                  <label className={labelCls}>Estado</label>
                                  <select value={food.preparation || 'cru'} onChange={e => setDietFood(mi, fi, 'preparation', e.target.value)} className={inputCls}>
                                    <option value="cru">Cru</option>
                                    <option value="cozido">Cozido</option>
                                    <option value="outro">Outro</option>
                                  </select>
                                </div>
                                <div>
                                  <label className={labelCls}>Kcal</label>
                                  <input type="number" value={food.calories} onChange={e => setDietFood(mi, fi, 'calories', e.target.value)} className={inputCls} placeholder="0" />
                                </div>
                                <div>
                                  <label className={labelCls}>Prot (g)</label>
                                  <input type="number" value={food.protein} onChange={e => setDietFood(mi, fi, 'protein', e.target.value)} className={inputCls} placeholder="0" />
                                </div>
                                <div>
                                  <label className={labelCls}>Carb (g)</label>
                                  <input type="number" value={food.carbs} onChange={e => setDietFood(mi, fi, 'carbs', e.target.value)} className={inputCls} placeholder="0" />
                                </div>
                                <div>
                                  <label className={labelCls}>Gord (g)</label>
                                  <input type="number" value={food.fat} onChange={e => setDietFood(mi, fi, 'fat', e.target.value)} className={inputCls} placeholder="0" />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <button type="submit" disabled={dietSaving}
                  className="self-start px-6 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50">
                  {dietSaving ? 'Salvando...' : 'Criar dieta'}
                </button>
              </form>
            )}

            {dietTemplates.length === 0 ? (
              <div className="text-center py-16 text-[#555] text-sm">Nenhuma dieta criada ainda.</div>
            ) : (
              <div className="flex flex-col gap-3">
                {dietTemplates.map(dt => (
                  <div key={dt.id} className="bg-[#121212] border border-white/5">
                    <div
                      className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-white/[0.02] transition-colors"
                      onClick={() => setExpandedDiet(expandedDiet === dt.id ? null : dt.id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <ChevronDown size={18} className={`text-[#555] flex-shrink-0 transition-transform ${expandedDiet === dt.id ? 'rotate-180' : ''}`} />
                        <div className="min-w-0">
                          <p className="font-medium text-sm">{dt.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {calcDietCals(dt.meals) > 0 && <span className="text-xs text-[#555] border border-white/10 px-2 py-0.5">{calcDietCals(dt.meals)} kcal/dia</span>}
                            {dt.description && <span className="text-xs text-[#555] truncate">{dt.description}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-3 flex-shrink-0">
                        <button onClick={e => { e.stopPropagation(); startEditDiet(dt); }} className="text-[#555] hover:text-white transition-colors p-1"><Edit2 size={16} /></button>
                        <button onClick={e => { e.stopPropagation(); handleDeleteDiet(dt.id); }} className="text-[#555] hover:text-red-400 transition-colors p-1"><Trash2 size={16} /></button>
                      </div>
                    </div>

                    {expandedDiet === dt.id && (
                      <div className="border-t border-white/5 px-5 pb-5 pt-4">
                        {editingDiet === dt.id ? (
                          <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <label className={labelCls}>Nome</label>
                                <input value={editDietForm.name || ''} onChange={e => setEditDietForm(f => ({...f, name: e.target.value}))} className={inputCls} />
                              </div>
                              <div>
                                <label className={labelCls}>Calorias (calculado)</label>
                                <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full">
                                  <span className="text-white font-medium">{calcDietCals(editDietForm.meals)} kcal</span>
                                </div>
                              </div>
                              <div className="sm:col-span-2">
                                <label className={labelCls}>Descrição</label>
                                <input value={editDietForm.description || ''} onChange={e => setEditDietForm(f => ({...f, description: e.target.value}))} className={inputCls} />
                              </div>
                              <div className="sm:col-span-2">
                                <label className={labelCls}>Observações</label>
                                <textarea value={editDietForm.notes || ''} onChange={e => setEditDietForm(f => ({...f, notes: e.target.value}))} className={`${inputCls} min-h-[60px] resize-y`} />
                              </div>
                            </div>
                            <div className="flex flex-col gap-3">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-[#555] uppercase tracking-widest">Refeições</span>
                                <button type="button" onClick={addEditMeal} className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors"><Plus size={11} /> Refeição</button>
                              </div>
                              {(editDietForm.meals || []).map((meal, mi) => (
                                <div key={mi} className="bg-[#0A0A0A] border border-white/5 p-4">
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="text-xs text-[#555]">Refeição {mi + 1}</span>
                                    {editDietForm.meals.length > 1 && <button type="button" onClick={() => removeEditMeal(mi)} className="text-[#555] hover:text-red-400"><Trash2 size={12} /></button>}
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 mb-3">
                                    <div>
                                      <label className={labelCls}>Nome</label>
                                      <input value={meal.name} onChange={e => setEditMeal(mi, 'name', e.target.value)} className={inputCls} placeholder="Café da manhã" />
                                    </div>
                                    <div>
                                      <label className={labelCls}>Horário sugerido</label>
                                      <input type="time" value={meal.time || ''} onChange={e => setEditMeal(mi, 'time', e.target.value)} className={inputCls} />
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 mb-3 flex-wrap">
                                    <span className={`${labelCls} mb-0 mr-1`}>Dias:</span>
                                    {ALL_DAYS.map(d => {
                                      const active = (meal.days || ALL_DAYS).includes(d);
                                      return (
                                        <button key={d} type="button" onClick={() => {
                                          const cur = meal.days || ALL_DAYS;
                                          setEditMeal(mi, 'days', active ? cur.filter(x => x !== d) : [...cur, d]);
                                        }} className={`px-2 py-0.5 text-xs border transition-colors ${active ? 'border-white/40 text-white' : 'border-white/10 text-[#444] hover:text-[#888]'}`}>
                                          {d.slice(0, 3)}
                                        </button>
                                      );
                                    })}
                                    <span className="text-xs text-[#444] ml-1">{(meal.days || ALL_DAYS).length}×/sem</span>
                                  </div>
                                  <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-[#555]">Alimentos</span>
                                      <button type="button" onClick={() => addEditFood(mi)} className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-1 transition-colors"><Plus size={11} /> Alimento</button>
                                    </div>
                                    {(meal.foods || []).map((food, fi) => (
                                      <div key={fi} className="bg-[#121212] border border-white/5 p-3">
                                        <div className="flex justify-end mb-2">
                                          {meal.foods.length > 1 && <button type="button" onClick={() => removeEditFood(mi, fi)} className="text-[#555] hover:text-red-400"><Trash2 size={11} /></button>}
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                          <div className="col-span-2">
                                            <label className={labelCls}>Alimento</label>
                                            <input value={food.name || ''} onChange={e => setEditFood(mi, fi, 'name', e.target.value)} className={inputCls} placeholder="Nome" />
                                          </div>
                                          <div><label className={labelCls}>Qtd</label><input value={food.quantity || ''} onChange={e => setEditFood(mi, fi, 'quantity', e.target.value)} className={inputCls} placeholder="100g" /></div>
                                          <div>
                                            <label className={labelCls}>Estado</label>
                                            <select value={food.preparation || 'cru'} onChange={e => setEditFood(mi, fi, 'preparation', e.target.value)} className={inputCls}>
                                              <option value="cru">Cru</option>
                                              <option value="cozido">Cozido</option>
                                              <option value="outro">Outro</option>
                                            </select>
                                          </div>
                                          <div><label className={labelCls}>Kcal</label><input type="number" value={food.calories || ''} onChange={e => setEditFood(mi, fi, 'calories', e.target.value)} className={inputCls} placeholder="0" /></div>
                                          <div><label className={labelCls}>Prot (g)</label><input type="number" value={food.protein || ''} onChange={e => setEditFood(mi, fi, 'protein', e.target.value)} className={inputCls} placeholder="0" /></div>
                                          <div><label className={labelCls}>Carb (g)</label><input type="number" value={food.carbs || ''} onChange={e => setEditFood(mi, fi, 'carbs', e.target.value)} className={inputCls} placeholder="0" /></div>
                                          <div><label className={labelCls}>Gord (g)</label><input type="number" value={food.fat || ''} onChange={e => setEditFood(mi, fi, 'fat', e.target.value)} className={inputCls} placeholder="0" /></div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => saveEditDiet(dt.id)} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors"><Check size={13} /> Salvar</button>
                              <button onClick={() => setEditingDiet(null)} className="flex items-center gap-2 px-4 py-2 border border-white/10 text-sm text-[#A3A3A3] hover:text-white transition-colors"><X size={13} /> Cancelar</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col gap-3">
                            {(!dt.meals || dt.meals.length === 0) ? (
                              <p className="text-sm text-[#555]">Sem refeições cadastradas.</p>
                            ) : dt.meals.map((meal, mi) => (
                              <div key={mi} className="bg-[#0A0A0A] border border-white/5 p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-medium text-sm">{meal.name || `Refeição ${mi + 1}`}</span>
                                  {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                                  {meal.days && meal.days.length < 7 && <span className="text-xs text-[#555]">{meal.days.map(d => d.slice(0,3)).join(', ')}</span>}
                                </div>
                                {meal.foods && meal.foods.length > 0 && (
                                  <table className="w-full text-xs">
                                    <tbody>
                                      {meal.foods.map((f, fi) => (
                                        <tr key={fi} className="border-b border-white/5 last:border-0">
                                          <td className="py-1 pr-3 text-[#E5E5E5]">{f.name}</td>
                                          <td className="py-1 px-2 text-[#555]">{f.quantity}</td>
                                          <td className="py-1 px-2 text-[#555]">{f.preparation && f.preparation !== 'cru' ? f.preparation : ''}</td>
                                          <td className="py-1 px-2 text-[#555]">{f.calories ? `${f.calories}kcal` : ''}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              </div>
                            ))}
                            {dt.notes && <p className="text-xs text-[#555] mt-1">{dt.notes}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {alertMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#121212] border border-red-500/30 text-red-400 text-sm px-4 py-3 flex items-center gap-3 shadow-xl">
          {alertMsg}
          <button onClick={() => setAlertMsg('')} className="text-[#555] hover:text-white ml-2">✕</button>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#121212] border border-white/10 p-6 w-full max-w-sm">
            <p className="text-sm text-[#E5E5E5] mb-6 leading-relaxed">{confirm.message}</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm text-[#555] hover:text-white transition-colors">
                Cancelar
              </button>
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                className="px-4 py-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm hover:bg-red-500/20 transition-colors">
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
